import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  applyThemePreferences,
  readColorTheme,
  THEME_CHANGED_EVENT,
  THEME_PALETTES,
  writeColorTheme,
  type ColorTheme,
} from './themeSettings';
import {
  applyDarkMode,
  DARK_MODE_CHANGED_EVENT,
  readDarkMode,
  writeDarkMode,
} from './darkMode';
import type { ThemeTransitionOrigin } from './themeTransition';
import { getCurrentUser, setCurrentUser, USER_CHANGED_EVENT } from './permissions';
import { authApi, hasAuthToken } from '../api';

type ThemeContextValue = {
  colorTheme: ColorTheme;
  setColorTheme: (theme: ColorTheme) => void;
  darkMode: boolean;
  setDarkMode: (enabled: boolean, origin?: ThemeTransitionOrigin) => void;
  toggleDarkMode: (origin?: ThemeTransitionOrigin) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isColorTheme(v: unknown): v is ColorTheme {
  return typeof v === 'string' && THEME_PALETTES.some((p) => p.id === v);
}

/** Apply theme from signed-in user (cloud) onto this device. */
export function syncThemeFromUser(
  user: { uiPreferences?: { colorTheme?: string; darkMode?: boolean } } | null | undefined
): void {
  const remote = user?.uiPreferences?.colorTheme;
  if (isColorTheme(remote)) {
    if (readColorTheme() !== remote) {
      writeColorTheme(remote);
    } else {
      applyThemePreferences(remote);
    }
  }

  if (typeof user?.uiPreferences?.darkMode === 'boolean') {
    const remoteDark = user.uiPreferences.darkMode;
    if (readDarkMode() !== remoteDark) {
      writeDarkMode(remoteDark);
      applyDarkMode(remoteDark, false);
    }
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [colorTheme, setColorThemeState] = useState<ColorTheme>(() => readColorTheme());
  const [darkMode, setDarkModeState] = useState<boolean>(() => readDarkMode());

  useEffect(() => {
    const sync = () => {
      syncThemeFromUser(getCurrentUser());
      setColorThemeState(readColorTheme());
      setDarkModeState(readDarkMode());
    };
    sync();
    window.addEventListener(THEME_CHANGED_EVENT, sync);
    window.addEventListener(DARK_MODE_CHANGED_EVENT, sync);
    window.addEventListener(USER_CHANGED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(THEME_CHANGED_EVENT, sync);
      window.removeEventListener(DARK_MODE_CHANGED_EVENT, sync);
      window.removeEventListener(USER_CHANGED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    applyThemePreferences(colorTheme);
  }, [colorTheme]);

  useEffect(() => {
    applyDarkMode(darkMode, false);
  }, [darkMode]);

  const persistUiPrefs = useCallback((patch: { colorTheme?: ColorTheme; darkMode?: boolean }) => {
    if (!hasAuthToken()) return;
    void authApi
      .updateUiPreferences(patch)
      .then((res) => {
        if (res.user) {
          const prev = getCurrentUser();
          setCurrentUser({ ...(prev || res.user), ...res.user });
        }
      })
      .catch(() => {
        /* local prefs still applied */
      });
  }, []);

  const setColorTheme = useCallback(
    (theme: ColorTheme) => {
      writeColorTheme(theme);
      setColorThemeState(theme);
      applyThemePreferences(theme);
      persistUiPrefs({ colorTheme: theme });
    },
    [persistUiPrefs]
  );

  const setDarkMode = useCallback(
    (enabled: boolean, origin?: ThemeTransitionOrigin) => {
      writeDarkMode(enabled);
      setDarkModeState(enabled);
      applyDarkMode(enabled, Boolean(origin), origin);
      persistUiPrefs({ darkMode: enabled });
    },
    [persistUiPrefs]
  );

  const toggleDarkMode = useCallback(
    (origin?: ThemeTransitionOrigin) => {
      setDarkMode(!darkMode, origin);
    },
    [darkMode, setDarkMode]
  );

  const value = useMemo(
    () => ({ colorTheme, setColorTheme, darkMode, setDarkMode, toggleDarkMode }),
    [colorTheme, setColorTheme, darkMode, setDarkMode, toggleDarkMode]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useThemeSettings() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      colorTheme: readColorTheme(),
      setColorTheme: (_theme: ColorTheme) => {},
      darkMode: readDarkMode(),
      setDarkMode: (_enabled: boolean) => {},
      toggleDarkMode: () => {},
    };
  }
  return ctx;
}
